import { useRef } from "react";
import { Plus, Trash2 } from "lucide-react";
import { STATUS_COLORS, DELTA_COLORS, CELL_PADDING, CARD_BG, CARD_BG_HOVER } from "./constants";

export default function AssignmentBlock({
  assignment,
  onClick,
  onDragStart,
  onDragEnd,
  onResizeStart,
  onFillHandleStart,
  isResizing,
  resizePreviewHours,
  isDragging,
  isModified,
  isNew,
  isDeleted,
  showSollIst = true,
  isFillDragging,
  isSelected,
  onSelect,
  onQuickDelete,
  zoom = "standard",
  onOpenInTimesheet,
}) {
  const statusConf = STATUS_COLORS[assignment.status] || STATUS_COLORS.planned;
  const displayHours = isResizing ? resizePreviewHours : assignment.planned_hours;
  const actualHours = assignment.actual_hours;
  const isRealized = !!assignment.realized;
  const realizedHours = assignment.realized_hours || 0;
  const deltaStatus = assignment.delta_status;
  const deltaStyle = DELTA_COLORS[deltaStatus] || DELTA_COLORS.ok;
  const isCompact = zoom === "compact";
  const isLarge = zoom === "large";
  const pColor = assignment.project_color || "var(--tga-blue)";
  const projectName = assignment.project_name || "";

  // Click-delay-Pattern: erlaubt Single-Click (Navigate) UND Double-Click (Edit-Dialog).
  // Single-Click feuert erst nach 220ms, falls kein zweiter Klick eintrifft.
  const clickTimerRef = useRef(null);

  // Status-Logik (User-Wunsch 05.05.2026):
  // - Geplant   = Assignment existiert, kein time_entry
  // - Umgesetzt = time_entry existiert (assignment.realized = true vom Backend)
  const liveStatus = isRealized
    ? { label: "Umgesetzt", color: "#10b981", bg: "rgba(16,185,129,0.15)", border: "rgba(16,185,129,0.4)" }
    : { label: "Geplant",   color: "#60a5fa", bg: "rgba(96,165,250,0.12)", border: "rgba(96,165,250,0.35)" };

  // Dynamic font size based on character count.
  // Targets: kurzer Name -> Standardgröße. Langer Name -> kleiner, aber weiterhin gut lesbar.
  // Floor liegt bei 10/11px, weicht zu 2-Zeilen-Wrap aus statt darunter zu fallen.
  const titleFontSize = (() => {
    const len = projectName.length;
    if (isCompact) {
      if (len <= 14) return 11;
      if (len <= 22) return 10;
      return 10; // compact: hard floor, wrap kicks in
    }
    if (isLarge) {
      if (len <= 14) return 17;
      if (len <= 22) return 15;
      if (len <= 32) return 13;
      return 12;
    }
    // standard
    if (len <= 14) return 15;
    if (len <= 22) return 13;
    if (len <= 32) return 12;
    return 11;
  })();

  // Tooltip content: vollstaendiger Name + Status + Stunden + Mitarbeiter + Hinweis
  const tooltipText = [
    projectName,
    `Status: ${liveStatus.label}`,
    isRealized
      ? `Stunden Ist: ${realizedHours.toFixed(1)}h (Plan: ${displayHours}h)`
      : `Stunden Plan: ${displayHours}h`,
    assignment.user_name ? `Mitarbeiter: ${assignment.user_name}` : null,
    "",
    "Klick: Zum Stundenkonto öffnen",
    "Doppelklick: Bearbeiten",
  ].filter(s => s !== null).join("\n");

  if (isDeleted) {
    return (
      <div className="absolute inset-0 flex items-center justify-center rounded-lg z-10"
        style={{ margin: CELL_PADDING, background: "rgba(239,68,68,0.08)", border: "1px dashed rgba(239,68,68,0.3)" }}>
        <span className="text-[12px] text-red-400 line-through">{assignment.project_name}</span>
      </div>
    );
  }

  return (
    <div
      draggable={!isResizing && !isFillDragging}
      onDragStart={(e) => !isResizing && !isFillDragging && onDragStart(e, assignment)}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        // Multi-Select Power-User: Ctrl/Cmd/Shift hält Auswahl-Verhalten (sofort, kein Delay)
        if (e.ctrlKey || e.metaKey || e.shiftKey) {
          if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
          onSelect?.(assignment.id, true);
          return;
        }
        // Click-Delay: warte 220ms ob Doubleclick kommt; sonst Single-Click → Navigate
        if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
        clickTimerRef.current = setTimeout(() => {
          clickTimerRef.current = null;
          if (onOpenInTimesheet) {
            onOpenInTimesheet(assignment);
          } else {
            onSelect?.(assignment.id, false);
          }
        }, 220);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        // Double-Click cancelt das pending Single-Click und öffnet Edit-Dialog
        if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
        if (!isResizing && !isFillDragging) onClick(assignment);
      }}
      className={`group absolute inset-0 flex flex-col cursor-pointer select-none rounded-lg border-l-[4px] ${
        isDragging ? "opacity-40 scale-95" : ""
      }`}
      style={{
        margin: CELL_PADDING,
        backgroundColor: isSelected ? CARD_BG_HOVER : CARD_BG,
        borderLeftColor: pColor,
        border: `1px solid var(--tga-border-strong)`,
        borderLeft: `4px solid ${pColor}`,
        boxShadow: `0 2px 8px var(--tga-shadow-overlay, rgba(0,0,0,0.15)), 0 0 0 1px var(--tga-border), inset 0 1px 0 var(--tga-border)`,
        transition: "transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.03)";
        e.currentTarget.style.boxShadow = `0 4px 16px var(--tga-shadow-overlay, rgba(0,0,0,0.2)), 0 0 12px ${pColor}25, inset 0 1px 0 var(--tga-border)`;
        e.currentTarget.style.backgroundColor = CARD_BG_HOVER;
        e.currentTarget.style.zIndex = "20";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = `0 2px 8px var(--tga-shadow-overlay, rgba(0,0,0,0.15)), 0 0 0 1px var(--tga-border), inset 0 1px 0 var(--tga-border)`;
        e.currentTarget.style.backgroundColor = isSelected ? CARD_BG_HOVER : CARD_BG;
        e.currentTarget.style.zIndex = "";
      }}
      data-testid={`assignment-${assignment.id}`}
      data-assignment-id={assignment.id}
      title={tooltipText}
    >
      {/* Modified / New indicator */}
      {(isModified || isNew) && (
        <div
          className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full z-30"
          style={{ backgroundColor: isNew ? "#4ADE80" : "#FACC15" }}
        />
      )}

      {/* Quick Delete */}
      <button
        className="absolute top-1.5 right-1.5 p-0.5 rounded opacity-0 group-hover:opacity-60 hover:!opacity-100 z-30 transition-opacity"
        style={{ color: "var(--tga-text-secondary)" }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#F87171"; e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.1)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tga-text-secondary)"; e.currentTarget.style.backgroundColor = "transparent"; }}
        onClick={(e) => { e.stopPropagation(); onQuickDelete?.(assignment); }}
        title="Loeschen"
      >
        <Trash2 className={isCompact ? "w-3 h-3" : "w-3.5 h-3.5"} />
      </button>

      {/* Card Content */}
      <div className={`flex-1 flex flex-col overflow-hidden ${isCompact ? "p-1.5 gap-0.5" : isLarge ? "p-3 gap-1.5" : "p-2 gap-1"}`}>
        {/* TOP: Project Name — vollstaendige Anzeige, dynamische Font-Size, max 2 Zeilen */}
        <div
          className="font-bold leading-snug flex-shrink-0"
          style={{
            color: "var(--tga-text-bright)",
            fontSize: `${titleFontSize}px`,
            display: "-webkit-box",
            WebkitLineClamp: isCompact ? 2 : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
            hyphens: "auto",
            letterSpacing: "-0.01em",
          }}
          data-testid="assignment-project-name"
        >
          {projectName}
        </div>

        {/* MIDDLE: Spacer to push status to bottom */}
        <div className="flex-1" />

        {/* BOTTOM: Status-Badge — gross, klar, statt Zeit/Stunden (User-Wunsch 05.05.2026) */}
        <div className="flex-shrink-0">
          <span
            className={`inline-flex items-center gap-1 rounded-md font-bold ${isCompact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-1"}`}
            style={{
              backgroundColor: liveStatus.bg,
              color: liveStatus.color,
              border: `1px solid ${liveStatus.border}`,
            }}
            data-testid={`assignment-status-${isRealized ? "realized" : "planned"}`}
          >
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: liveStatus.color }} />
            {liveStatus.label}
            {isRealized && realizedHours > 0 && (
              <span className="font-mono opacity-80">· {realizedHours.toFixed(1)}h</span>
            )}
          </span>
        </div>
      </div>

      {/* Resize handles */}
      <div className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 z-20 transition-opacity"
        style={{ borderRadius: "8px 0 0 8px" }}
        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onResizeStart(e, assignment, "left"); }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(59,130,246,0.3)")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")} />
      <div className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 z-20 transition-opacity"
        style={{ borderRadius: "0 8px 8px 0" }}
        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onResizeStart(e, assignment, "right"); }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(59,130,246,0.3)")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")} />

      {/* Fill handle */}
      <div
        className="absolute bottom-1 right-1 w-4 h-4 rounded cursor-crosshair opacity-0 group-hover:opacity-100 z-30 flex items-center justify-center transition-opacity"
        style={{ backgroundColor: pColor, boxShadow: `0 0 4px ${pColor}60` }}
        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onFillHandleStart?.(e, assignment); }}
        title="Ziehen zum Kopieren"
      >
        <Plus className="w-2.5 h-2.5 text-white" />
      </div>
    </div>
  );
}
